import React, {useEffect, useRef, useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useTranslation} from 'react-i18next';

import type {CandidateTally, LiveState} from '../lib/use-live-stream';
import {Disclosure} from '@/shared/ui/disclosure';
import {StatTile, type StatTone} from '@/shared/ui/stat-tile';
import {AppText} from '@/shared/ui/text';
import {spacing, useTheme} from '@/shared/ui/theme';

/**
 * The full player's diagnostics as KPI tiles instead of a line of jargon.
 *
 * Each tile answers one question an operator or support actually asks — is it live,
 * which path, what picture, is it smooth, how heavy, is anything lost — and its tone
 * says whether the answer is fine. The raw lines (ICE agent, SDP summaries, the first
 * candidate) stay behind «Технические детали»: they are what turns a support call into
 * a one-line answer, but they are not something to read while watching.
 */
const styles = StyleSheet.create({
  root: {gap: spacing(3)},
  grid: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing(2)},
  // Grow from a common basis: two per row on a phone, three to six on wider screens,
  // and the last row stretches instead of leaving a hole.
  cell: {flexBasis: 150, flexGrow: 1},
  raw: {gap: spacing(1)},
});

/** H.264 profile from `profile-level-id`: the first byte is the profile_idc. */
export function h264ProfileName(plid: string | null): string | null {
  if (!plid) {
    return null;
  }
  const idc = plid.slice(0, 2).toLowerCase();
  return idc === '42' ? 'Baseline' : idc === '4d' ? 'Main' : idc === '64' ? 'High' : plid;
}

/** kbit/s from two (bytes, time) samples; null until there are two. */
export function bitrateKbps(
  prev: {bytes: number; at: number} | null,
  bytes: number,
  at: number,
): number | null {
  if (!prev || at <= prev.at || bytes < prev.bytes) {
    return null;
  }
  return Math.round(((bytes - prev.bytes) * 8) / (at - prev.at));
}

function tallyLine(t: CandidateTally): string {
  const list = (m: Record<string, number>) =>
    Object.entries(m)
      .map(([k, n]) => `${k} ${n}`)
      .join(', ') || '0';
  return [
    t.gathering ? `gather ${t.gathering}` : null,
    `rx ${list(t.rx)} (added ${t.rxAdded}${t.rxRejected ? `, rejected ${t.rxRejected}` : ''})`,
    t.rxError ? `err ${t.rxError}` : null,
    `tx ${list(t.tx)}`,
  ]
    .filter(Boolean)
    .join(' · ');
}

export function LiveStats({state}: {state: LiveState}) {
  const {t} = useTranslation();
  const p = useTheme();
  const [open, setOpen] = useState(false);

  // Bitrate is the rate of change of the byte counter between polls.
  const sample = useRef<{bytes: number; at: number} | null>(null);
  const [kbps, setKbps] = useState<number | null>(null);
  const bytes = state.video?.bytes;
  useEffect(() => {
    if (bytes == null) {
      sample.current = null;
      setKbps(null);
      return;
    }
    const now = Date.now();
    const rate = bitrateKbps(sample.current, bytes, now);
    if (rate !== null) {
      setKbps(rate);
    }
    sample.current = {bytes, at: now};
  }, [bytes]);

  const v = state.video;
  const playing = state.phase === 'playing';
  const failed = state.phase === 'error';

  const phaseTone: StatTone = playing ? 'good' : failed ? 'bad' : 'warn';
  const routeTone: StatTone =
    state.route === 'direct' ? 'good' : state.route === 'relay' ? 'info' : 'neutral';
  const fpsTone: StatTone =
    v?.fps == null ? 'neutral' : v.fps >= 8 ? 'good' : v.fps >= 4 ? 'warn' : 'bad';
  const lost = v ? v.framesDropped : 0;
  const lossShare = v && v.framesReceived > 0 ? lost / v.framesReceived : 0;
  const lossTone: StatTone = !v ? 'neutral' : lossShare > 0.05 ? 'bad' : lossShare > 0 ? 'warn' : 'good';
  const codec = v?.codec ? [v.codec, h264ProfileName(v.profile)].filter(Boolean).join(' ') : null;

  const tally = state.tally;
  const raw = [
    state.ice ? `ice ${state.ice}` : null,
    tally ? tallyLine(tally) : null,
    tally?.agent ? `agent: ${tally.agent}` : null,
    tally?.offer ? `offer: ${tally.offer}` : null,
    tally?.firstRx ? `rx[0]: ${tally.firstRx}` : null,
    tally?.answer ? `answer: ${tally.answer}` : null,
    v?.decoder ? `decoder: ${v.decoder}` : null,
  ].filter((l): l is string => !!l);

  return (
    <View style={styles.root} testID="live-stats">
      <View style={styles.grid}>
        <StatTile
          style={styles.cell}
          compact
          label={t('live.stats.state')}
          value={t(`live.stats.phase.${state.phase}`)}
          tone={phaseTone}
          hint={state.ice ? t('live.stats.ice', {state: state.ice}) : null}
        />
        <StatTile
          style={styles.cell}
          compact
          label={t('live.stats.route')}
          value={state.route ? t(`live.route.${state.route}`) : '—'}
          tone={routeTone}
          hint={state.route === 'relay' ? t('live.stats.relayHint') : null}
        />
        <StatTile
          style={styles.cell}
          compact
          label={t('live.stats.picture')}
          value={v?.width && v?.height ? `${v.width}×${v.height}` : '—'}
          hint={codec}
        />
        <StatTile
          style={styles.cell}
          compact
          label={t('live.stats.fps')}
          value={v?.fps != null ? t('live.stats.fpsValue', {n: Math.round(v.fps)}) : '—'}
          tone={fpsTone}
        />
        <StatTile
          style={styles.cell}
          compact
          label={t('live.stats.bitrate')}
          value={kbps != null ? t('live.stats.kbps', {n: kbps}) : '—'}
          tone="info"
        />
        <StatTile
          style={styles.cell}
          compact
          label={t('live.stats.frames')}
          value={v ? v.framesDecoded : '—'}
          tone={lossTone}
          hint={v ? t('live.stats.lost', {n: lost}) : null}
        />
      </View>

      {raw.length > 0 ? (
        <Disclosure
          title={t('live.stats.details')}
          summary={t('live.stats.detailsHint')}
          open={open}
          onToggle={() => setOpen(o => !o)}>
          <View style={styles.raw}>
            {raw.map(line => (
              <AppText key={line} variant="mono" color={p.mutedForeground} selectable>
                {line}
              </AppText>
            ))}
          </View>
        </Disclosure>
      ) : null}
    </View>
  );
}
